import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createMyScheduleEvent,
  updateMyScheduleEvent,
  deleteMyScheduleEvent,
  type ScheduleEvent,
  type ScheduleEventCreate,
} from "@/api/client";
import { localInputToUTC, utcToLocalInput } from "@/lib/tz";
import { CategorySelect } from "./scheduleEventCategories";

const INPUT_CLASS =
  "input";
const BTN_PRIMARY =
  "btn btn--accent";
const BTN_GHOST =
  "btn btn--ghost";
const BTN_DANGER =
  "btn btn--bad";

type Props = {
  festivalId: string;
  festivalTimezone: string;
  existing?: ScheduleEvent;
  onClose: () => void;
};

export function ScheduleEventDialog({
  festivalId,
  festivalTimezone,
  existing,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<ScheduleEventCreate>({
    day: existing?.day ?? "",
    start_datetime: existing?.start_datetime ?? "",
    end_datetime: existing?.end_datetime ?? "",
    category: existing?.category ?? "soundcheck",
    title: existing?.title ?? "",
    location: existing?.location ?? "",
    notes: existing?.notes ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["artist-portal", festivalId] });
    qc.invalidateQueries({ queryKey: ["artist-portal", festivalId, "itinerary"] });
    qc.invalidateQueries({ queryKey: ["artist-portal", festivalId, "schedule-events"] });
  };

  const save = useMutation({
    mutationFn: () =>
      existing
        ? updateMyScheduleEvent(festivalId, existing.id, form)
        : createMyScheduleEvent(festivalId, form),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const del = useMutation({
    mutationFn: () => deleteMyScheduleEvent(festivalId, existing!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg card p-6 max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <h2 className="mb-4 text-lg font-semibold text-ink">
          {existing ? "Edit event" : "Add event"}
        </h2>
        <div className="space-y-3">
          <Field label="Title">
            <input
              className={INPUT_CLASS}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </Field>
          <Field label="Category">
            <CategorySelect
              value={form.category}
              onChange={(category) => setForm({ ...form, category })}
            />
          </Field>
          <Field label="Day">
            <input
              type="date"
              className={INPUT_CLASS}
              value={form.day}
              onChange={(e) => setForm({ ...form, day: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label={`Start (local — ${festivalTimezone})`}>
              <input
                type="datetime-local"
                className={INPUT_CLASS}
                value={
                  form.start_datetime
                    ? utcToLocalInput(form.start_datetime, festivalTimezone)
                    : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    start_datetime: e.target.value
                      ? localInputToUTC(e.target.value, festivalTimezone)
                      : "",
                  })
                }
              />
            </Field>
            <Field label={`End (local — ${festivalTimezone})`}>
              <input
                type="datetime-local"
                className={INPUT_CLASS}
                value={
                  form.end_datetime
                    ? utcToLocalInput(form.end_datetime, festivalTimezone)
                    : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    end_datetime: e.target.value
                      ? localInputToUTC(e.target.value, festivalTimezone)
                      : "",
                  })
                }
              />
            </Field>
          </div>
          <Field label="Location (optional)">
            <input
              className={INPUT_CLASS}
              value={form.location ?? ""}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
            />
          </Field>
          <Field label="Notes (optional)">
            <textarea
              className={INPUT_CLASS}
              rows={3}
              value={form.notes ?? ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Field>
        </div>
        {error && <div className="mt-3 text-sm text-bad">{error}</div>}
        <div className="mt-4 flex justify-between">
          {existing ? (
            <button
              className={BTN_DANGER}
              onClick={() => del.mutate()}
              disabled={del.isPending || save.isPending}
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <button className={BTN_GHOST} onClick={onClose}>
              Cancel
            </button>
            <button
              className={BTN_PRIMARY}
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Saving…" : existing ? "Save" : "Add"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-ink-3">{label}</span>
      {children}
    </label>
  );
}
